import { Injectable } from '@angular/core';
// Database imports
import { MotesPaths } from '@motes-sdk/models/motes-paths';

// Rx JS imports
import { Observable, of, combineLatest, from } from 'rxjs';
import { map, first, shareReplay } from 'rxjs/operators';

// Models
import { Product, ProductType } from '../models/product';
import { ListenEvent } from './database';
import { DatabaseService } from './database.service';
import { RestService } from './rest.service';

@Injectable({ providedIn: 'root' })
export class ProductService {
    // Cache for retrieving products faster
    public productReplayMap: { [productKey: string]: Observable<Product>; } = {};

    constructor(private af: DatabaseService, private restService: RestService) {
    }

    public init() {
    }

    public generateKey(): string {
        return this.af.database.ref().push().key;
    }

    public async saveProduct(product: Product): Promise<void> {
        // Get new id for mote
        if(!product.key) {
            // Generate new key
            product.key = this.af.database.ref().push().key;
        }
        if(!product.createdOn) {
            product.createdOn = this.af.serverTime();
        }

        // Create / update product and price
        await this.af.database.ref(MotesPaths.PRODUCT_DB_PATH(product.key)).update(product.toJson());
        // Update tour price
        await this.updateTourPrice(product.tourKey);
    }

    public async saveProductStripeId(productKey: string, stripeId: string): Promise<boolean> {
        return this.af.database.ref(`products/${productKey}`).update({ 'stripeId': stripeId }).then(() => true).catch(() => false);
    }

    public async saveProductStripePriceKey(productKey: string, stripePriceKey: string): Promise<boolean> {
        return this.af.database.ref(`products/${productKey}`).update({ 'stripePriceKey': stripePriceKey }).then(() => true).catch(() => false);
    }

    /**
     * Update the price of the tour with the cheapest product associated
     */
    private async updateTourPrice(tourKey: string) {
        // Find all products associated with the tour
        let products = await this.watchProductsOfTour(tourKey).pipe(first()).toPromise();
        products = products.filter((product) => (product.type == ProductType.Tour) && !product.parent);

        if(products.length <= 0) return;

        // Sort by cheapest product
        products.sort((a, b) => a.price - b.price);
        let cheapestProduct = products[0];

        // Save price of cheapest product in tour
        await this.af.database.ref(MotesPaths.MOTE_DB_PATH(tourKey)).update({ price: cheapestProduct.price });
        if(cheapestProduct.price <= 0) await this.af.database.ref(`motes/${tourKey}`).update({ 'productLocked': false });
        // TODO: Consider adding the tour to associated products (however, this might be overkill to do on every product save)
    }

    private async deleteTourProduct(tourKey: string, productKey: string) {
        await this.af.database.ref(`motes/${tourKey}/products/${productKey}`).remove();
    }

    public async deleteProduct(product: Product): Promise<boolean> {
        if(!product.key || !product.project) return false;
        const productKey = product.key;
        const tourKey = product.tourKey;
        const stripeId = product.stripeId;

        // Delete product from db
        const productDelSuccess = await this.af.database.ref("products/" + product.key).remove().then(() => true).catch(() => false);
        if(!productDelSuccess) return false;
        // Update related tour if any
        if(tourKey) {
            await this.deleteTourProduct(tourKey, productKey);
            await this.updateTourPrice(tourKey);
        }
        // Delete Stripe product if any
        if(stripeId) {
            const stripeDelSuccess = await this.restService.deleteStripeProduct(stripeId);
            if(!stripeDelSuccess) return false;
        }

        return true;
    }

    public watchAllProducts(projectKey: string = null): Observable<Product[]> {
        const CHANGE_EVENTS: Array<ListenEvent> = [ListenEvent.added, ListenEvent.removed, ListenEvent.changed, ListenEvent.moved];
        let query = projectKey ? (ref) => ref.orderByChild('project').equalTo(projectKey) : null;
        // Get new list observable
        return this.af.list('products/', query).snapshotChanges(CHANGE_EVENTS).pipe(map((results) => {
            // Create array of Project objects from firebase object array
            return results.map((json) => {
                return new Product(json.key, json.payload.val());
            });
        }));
    }

    public watchProduct(productKey: string, order: number = 0): Observable<Product | null> {
        if(!productKey) return from([null]);
        // Return cached response
        if(this.productReplayMap[productKey]) return this.productReplayMap[productKey];

        // Get mote from DB and mapt to Mote object
        const productObs = this.af.object("products/" + productKey).snapshotChanges().pipe(map((json) => {
            return new Product(json.key, json.payload.val(), order);
        })).pipe(shareReplay(1));

        // Cache product response
        this.productReplayMap[productKey] = productObs;

        return productObs;
    }

    public getProduct(productKey: string): Promise<Product> {
        return this.watchProduct(productKey).pipe(first()).toPromise();
    }

    public watchProducts(projectKey: string, productKeys: string[]): Observable<Product[]> {
        if(!productKeys || productKeys.length <= 0) return of([]);

        let observables = productKeys.map((key, order) => this.watchProduct(key, order));
        // Merge all query observable outputs
        return combineLatest(...observables);
    }

    public watchProductChildren(parentKey: string): Observable<Product[]> {
        let query = (ref) => ref.orderByChild('parent').equalTo(parentKey);

        return this.af.database.list(MotesPaths.PRODUCT_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            let products = snapList.map((snap) => new Product(snap.key, snap.payload.val()));
            return products;
        }));
    }

    public watchProductsOfTour(tourKey: string): Observable<Product[]> {
        if(!tourKey) return of([]);

        let query = (ref) => ref.orderByChild('tourKey').equalTo(tourKey);

        return this.af.database.list(MotesPaths.PRODUCT_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            let products = snapList.map((snap) => new Product(snap.key, snap.payload.val()));
            return products;
        }));
    }

    public watchProductsWithWebhooks(): Observable<Product[]> {
        let query = (ref) => ref.orderByChild('purchaseWebhook').startAt(1);

        return this.af.database.list(MotesPaths.PRODUCT_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            let products = snapList.map((snap) => new Product(snap.key, snap.payload.val()));
            return products;
        }));
    }
}
